import { DISCIPLINES, getCalling, skill, type SkillId, skillsIn } from '@wyn/content';
import type { BoardIdView } from '@wyn/contracts';
import { useQuery } from '@tanstack/react-query';
import { Navigate } from '@tanstack/react-router';
import { useState } from 'react';
import { GameShell } from '../components/Layout.tsx';
import { ErrorNotice } from '../components/Notice.tsx';
import { PageTitle } from '../components/ui.tsx';
import { UI } from '../icons/ui-icons.ts';
import { boardsQuery } from '../lib/queries.ts';
import { coin } from '../lib/time.ts';
import { useMe } from '../lib/useMe.ts';
import { Loading } from './Landing.tsx';

/** Places shown (game-core `LEADERBOARD.top`). */
const TOP = 50;

const DISCIPLINE_NAME = { martial: 'Martial', craft: 'Craft', cunning: 'Cunning', cloister: 'Cloister' } as const;

const boardName = (b: BoardIdView) => (b === 'renown' ? 'Renown' : b === 'wealth' ? 'Wealth' : skill(b.slice('skill:'.length) as SkillId).name);

/** What a score reads as: Renown and XP as numbers, wealth as coin. */
const scoreText = (b: BoardIdView, n: number) => (b === 'wealth' ? coin(n) : b === 'renown' ? `${n.toLocaleString('en-GB')} Renown` : `${n.toLocaleString('en-GB')} XP`);

/** The realm's best: Renown, all coin held, and each skill. Banished players are left off while the ban holds. */
export function Leaderboards() {
  const me = useMe();
  const [board, setBoard] = useState<BoardIdView>('renown');
  const q = useQuery(boardsQuery(board));
  if (me.isPending) return <Loading />;
  if (!me.data?.player) return <Navigate to="/" />;

  return (
    <GameShell me={me.data} skew={me.skew}>
      <section className="panel space-y-4">
        <PageTitle icon={UI.boards}>Leaderboards</PageTitle>
        <div className="flex flex-wrap items-center gap-2">
          <div role="tablist" aria-label="Board" className="flex gap-2">
            {(['renown', 'wealth'] as const).map((b) => (
              <button key={b} type="button" role="tab" aria-selected={board === b} className={board === b ? 'btn' : 'btn-quiet'} onClick={() => setBoard(b)}>
                {boardName(b)}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-2 text-sm">
            <span className="text-ink-dim">Skill</span>
            <select className="field" aria-label="Skill board" value={board.startsWith('skill:') ? board : ''} onChange={(e) => e.target.value && setBoard(e.target.value as BoardIdView)}>
              <option value="">Choose a skill…</option>
              {DISCIPLINES.map((d) => (
                <optgroup key={d} label={DISCIPLINE_NAME[d]}>
                  {skillsIn(d).map((s) => <option key={s.id} value={`skill:${s.id}`}>{s.name}</option>)}
                </optgroup>
              ))}
            </select>
          </label>
        </div>
        <h2 className="heading">{boardName(board)}</h2>
        {q.isPending ? (
          <p className="text-ink-faint">Consulting the heralds…</p>
        ) : q.data ? (
          <>
            {q.data.entries.length === 0 ? (
              <p className="text-ink-faint">Nobody has made a name here yet.</p>
            ) : (
              <table className="w-full text-sm" aria-label={`${boardName(board)} leaderboard`}>
                <thead>
                  <tr className="smallcaps text-left text-ink-dim">
                    <th className="w-12 py-1">#</th>
                    <th>Name</th>
                    <th>Calling</th>
                    {q.data.entries[0]!.level !== null && <th className="text-right">Level</th>}
                    <th className="text-right">{board === 'wealth' ? 'Coin' : board === 'renown' ? 'Renown' : 'XP'}</th>
                  </tr>
                </thead>
                <tbody>
                  {q.data.entries.map((e) => (
                    <tr key={e.playerId} className={`border-t border-rule/60 ${e.playerId === me.data!.player!.id ? 'text-gilt' : ''}`}>
                      <td className="py-1 font-mono">{e.rank}</td>
                      <td>{e.name}</td>
                      <td className="text-ink-dim">{getCalling(e.calling).name}</td>
                      {e.level !== null && <td className="text-right font-mono">{e.level}</td>}
                      <td className="text-right font-mono">{board === 'wealth' ? coin(e.score) : e.score.toLocaleString('en-GB')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <p className="text-sm text-ink-dim" aria-label="Your standing">
              You: {scoreText(board, q.data.you.score)}
              {q.data.you.level !== null && <>, level {q.data.you.level}</>}
              {q.data.you.rank !== null ? <> · {ordinal(q.data.you.rank)} place</> : q.data.you.score > 0 ? <> · outside the top {TOP}</> : <> · not on the board yet</>}
            </p>
          </>
        ) : null}
        <ErrorNotice error={q.error} />
      </section>
    </GameShell>
  );
}

function ordinal(n: number) {
  const s = n % 100 >= 11 && n % 100 <= 13 ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[n % 10] ?? 'th';
  return `${n}${s}`;
}
